/* eslint-disable react/no-array-index-key */
import React, { useMemo, memo, useCallback, useRef } from 'react';

import { TableRow, TableCell } from '@material-ui/core';
import { Skeleton } from '@material-ui/lab';
import { v4 as uuid } from 'uuid';

import { resizeString } from 'utils';

import { CustomTooltip } from '../..';
import { getCellRenderer } from '../cellrenders';
import { DefineColumn, GenericObject, RenderComponentProps } from '../types';

interface TableRowDataProps {
  row: any;
  rows: any[];
  index: number;
  defineColumns: DefineColumn[];
  filterEntityType?: string | undefined;
  hiddenColumns?: string[];
  selectable?: boolean;
  onRowClick?(row: any): void;
  rowKeyTitle?: string;
  context?: any;
  id?: string;
  loading?: boolean;
}

const TableRowData: React.FC<TableRowDataProps> = ({
  row,
  rows,
  index,
  context,
  hiddenColumns,
  defineColumns,
  selectable,
  rowKeyTitle,
  onRowClick,
  filterEntityType,
  id,
  loading,
}) => {
  const tooltipRef = useRef(null);
  const cellRef = useRef(null);

  const rowMount: DefineColumn[] = useMemo(() => {
    const hidden = hiddenColumns
      ? [...hiddenColumns, 'id', 'type']
      : ['id', 'type'];
    return defineColumns.filter(column => !hidden.includes(column.id));
  }, [hiddenColumns, defineColumns]);

  const handleClick = useCallback(
    (event: React.MouseEvent) => {
      event.preventDefault();

      if (onRowClick) {
        event.stopPropagation();
        onRowClick(row);
      }
    },
    [onRowClick, row],
  );

  const renderCell = useCallback(
    (key, value: GenericObject) => {
      const defineColumn = defineColumns.find(column => column.id === key);

      if (defineColumn && defineColumn.renderComponent) {
        const { renderComponent } = defineColumn;
        const component = getCellRenderer(renderComponent);

        if (component) {
          return component({
            row,
            rows,
            value,
            index,
            context,
            filterEntityType,
            columnsLength: rowMount.length,
            ...defineColumn,
            columnId: defineColumn.id,
          } as RenderComponentProps);
        }

        return value;
      }

      if (defineColumn && defineColumn.hasTooltip) {
        const valueToFormat = defineColumn.valueFormatter
          ? defineColumn.valueFormatter(value, row)
          : value;
        return (
          <CustomTooltip
            ref={tooltipRef}
            maxWidth={defineColumn.maxTooltipSize || 150}
            labelValue={valueToFormat}
            showCircle={false}
            showTitle={false}
            minWidth={defineColumn.maxTooltipSize || 'fit-content'}
            placement="bottom-start"
          >
            {resizeString(
              String(valueToFormat),
              defineColumn.maxStringLength || 20,
            )}
          </CustomTooltip>
        );
      }

      if (defineColumn && defineColumn.valueFormatter) {
        return defineColumn.valueFormatter(value, row);
      }

      return value;
    },
    [defineColumns, filterEntityType, rowMount, row, context, index, rows],
  );

  return (
    <TableRow
      hover
      onClick={handleClick}
      tabIndex={-1}
      key={row.id}
      title={selectable && rowKeyTitle ? row[rowKeyTitle] : ''}
      style={{
        cursor:
          (handleClick || onRowClick) && selectable ? 'pointer' : undefined,
      }}
      id={id}
    >
      {rowMount.map(column => (
        <TableCell
          ref={cellRef}
          align={column?.textAlign || 'left'}
          key={`cell-${uuid()}`}
          id={`c-${row.id}-${column.id}`}
        >
          {loading ? (
            <Skeleton width="100%" height={50} style={{ transform: 'none' }} />
          ) : (
            renderCell(column.id, row[column.id])
          )}
        </TableCell>
      ))}
    </TableRow>
  );
};

export default memo(TableRowData);
